import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutGrid,
  KanbanSquare,
  BarChart3,
  Sparkles,
  BellRing,
  Brain,
  Inbox,
  SlidersHorizontal,
  ShieldCheck,
} from "lucide-react";
import { useRole, canAccess } from "@/lib/use-role";
import soapLogo from "@/assets/soap-logo.png";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

type NavRoute = "/loja" | "/pipeline" | "/solicitacoes" | "/dashboard" | "/notificacoes";

const items: { title: string; url: NavRoute; icon: typeof LayoutGrid }[] = [
  { title: "Flow", url: "/loja", icon: LayoutGrid },
  { title: "Pipeline", url: "/pipeline", icon: KanbanSquare },
  { title: "Solicitações", url: "/solicitacoes", icon: Inbox },
  { title: "Dashboard", url: "/dashboard", icon: BarChart3 },
  { title: "Notificações", url: "/notificacoes", icon: BellRing },
];

const configItems: {
  title: string;
  url: "/parametrizacao" | "/perfis";
  icon: typeof LayoutGrid;
}[] = [
  { title: "Parametrização", url: "/parametrizacao", icon: SlidersHorizontal },
  { title: "Perfis e permissões", url: "/perfis", icon: ShieldCheck },
];

export function AppSidebar() {
  const role = useRole();
  // `useRole` já assina o registry, então a barra reage a mudanças de perfil.
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const currentPath = useRouterState({
    select: (r) => r.location.pathname,
  });
  const isActive = (p: string) => currentPath === p || currentPath.startsWith(p + "/");
  const visibleItems = items.filter((i) => canAccess(role, i.url));
  const visibleConfig = configItems.filter((i) => canAccess(role, i.url));
  const showFit = canAccess(role, "fit");
  const showAdminIa = canAccess(role, "/admin-ia");
  const homeHref = visibleItems[0]?.url ?? "/notificacoes";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <Link
          to={homeHref}
          className={`flex flex-col items-center gap-1 px-2 py-3 ${collapsed ? "px-0" : ""}`}
        >
          <img src={soapLogo} alt="SOAP" className={collapsed ? "h-7 w-auto" : "h-12 w-auto"} />
          {!collapsed && (
            <span
              className="text-[11px] font-bold uppercase tracking-[0.22em]"
              style={{ color: "#FF5A00" }}
            >
              Flow
            </span>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Operação</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link to={item.url} className="flex items-center gap-2">
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {(showFit || showAdminIa) && (
          <SidebarGroup>
            <SidebarGroupLabel>IA</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {showFit && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/fit")}>
                      <Link to="/fit" className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        {!collapsed && <span>Fit de Alocação</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {showAdminIa && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/admin-ia")}>
                      <Link to="/admin-ia" className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-primary" />
                        {!collapsed && <span>Alimentação da IA</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {visibleConfig.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Configuração</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleConfig.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                      <Link to={item.url} className="flex items-center gap-2">
                        <item.icon className="h-4 w-4" />
                        {!collapsed && <span>{item.title}</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
